import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getGiftCatalog } from '../../api/gifts'
import { likeListing, saveListing, shareListing, unlikeListing, unsaveListing } from '../../api/store'

type Props = {
  listingId: string
  sellerAvatarUrl?: string | null
  sellerProfilePublicId?: string | null
  shareUrl?: string | null
  initialLiked?: boolean
  initialSaved?: boolean
  initialLikes?: number
  initialSaves?: number
  initialShares?: number
  onOpenDetail?: () => void
  onOpenComments?: () => void
  onOpenGift?: () => void
}

export default function MobileStoreActionBar({
  listingId,
  sellerAvatarUrl,
  sellerProfilePublicId,
  shareUrl,
  initialLiked = false,
  initialSaved = false,
  initialLikes,
  initialSaves,
  initialShares,
  onOpenDetail,
  onOpenComments,
  onOpenGift,
}: Props) {
  const navigate = useNavigate()
  const [liked, setLiked] = useState<boolean>(initialLiked)
  const [saved, setSaved] = useState<boolean>(initialSaved)
  const [likes, setLikes] = useState<number | null>(typeof initialLikes === 'number' ? initialLikes : null)
  const [saves, setSaves] = useState<number | null>(typeof initialSaves === 'number' ? initialSaves : null)
  const [shares, setShares] = useState<number | null>(typeof initialShares === 'number' ? initialShares : null)

  useEffect(() => { setLiked(initialLiked) }, [initialLiked])
  useEffect(() => { setSaved(initialSaved) }, [initialSaved])

  const authGuard = useCallback((fn: () => Promise<void>) => {
    return fn().catch((err: any) => {
      const status = err?.response?.status
      if (status === 401) navigate('/login')
      // eslint-disable-next-line no-console
      else console.warn('Action failed', err)
    })
  }, [navigate])

  const openDetail = useCallback(() => {
    if (onOpenDetail) return onOpenDetail()
    navigate(`/store/i/${listingId}`)
  }, [listingId, navigate, onOpenDetail])

  const openAvatar = useCallback(() => {
    if (sellerProfilePublicId) navigate(`/people/i/${sellerProfilePublicId}`)
    else openDetail()
  }, [navigate, openDetail, sellerProfilePublicId])

  const openComments = useCallback(() => {
    if (onOpenComments) return onOpenComments()
    openDetail()
  }, [onOpenComments, openDetail])

  const prefetchGifts = useCallback(() => { getGiftCatalog().catch(() => null) }, [])
  const openGift = useCallback(() => {
    prefetchGifts()
    if (onOpenGift) return onOpenGift()
    // Store gifts aren't wired yet; open detail as a reasonable fallback.
    openDetail()
  }, [onOpenGift, openDetail, prefetchGifts])

  const toggleLike = useCallback(() => {
    const next = !liked
    setLiked(next)
    setLikes((c) => (typeof c === 'number' ? Math.max(0, c + (next ? 1 : -1)) : c))
    authGuard(async () => {
      const res = next ? await likeListing(listingId) : await unlikeListing(listingId)
      setLiked(res.liked)
      setLikes(res.likes)
    })
  }, [authGuard, liked, listingId])

  const toggleSave = useCallback(() => {
    const next = !saved
    setSaved(next)
    setSaves((c) => (typeof c === 'number' ? Math.max(0, c + (next ? 1 : -1)) : c))
    authGuard(async () => {
      const res = next ? await saveListing(listingId) : await unsaveListing(listingId)
      setSaved(res.saved)
      setSaves(res.saves)
    })
  }, [authGuard, listingId, saved])

  const doShare = useCallback(async () => {
    const url = shareUrl || `${window.location.origin}/store/i/${listingId}`
    const title = 'Check this out on Pixeloop Store'
    try {
      if ((navigator as any).share) await (navigator as any).share({ url, title })
      else await navigator.clipboard.writeText(url)
      setShares((c) => (typeof c === 'number' ? c + 1 : c))
      const res = await shareListing(listingId)
      setShares(res.shares)
    } catch {}
  }, [listingId, shareUrl])

  const ActionBtn = useMemo(() =>
    function ActionBtn({ onClick, label, children }: { onClick?: () => void; label: string; children: React.ReactNode }) {
      return (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onClick?.() }}
          aria-label={label}
          className="pointer-events-auto h-11 w-11 rounded-full border border-white/10 bg-black/35 backdrop-blur hover:bg-black/45 flex items-center justify-center"
        >
          {children}
        </button>
      )
    }
  , [])

  const Count = ({ n }: { n: number | null }) => (
    <div className="mt-1 text-[11px] text-white/85 drop-shadow">{typeof n === 'number' ? (n > 9999 ? `${Math.round(n / 1000)}k` : n) : ''}</div>
  )

  return (
    <div className="pointer-events-none absolute right-3 bottom-[calc(1rem+env(safe-area-inset-bottom))] flex flex-col items-center gap-4">
      {/* Avatar (top) */}
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); openAvatar() }}
        aria-label="Open seller"
        className="pointer-events-auto h-11 w-11 rounded-full overflow-hidden border border-white/25 bg-black/35 backdrop-blur"
      >
        {sellerAvatarUrl ? <img src={sellerAvatarUrl} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-white/10" />}
      </button>

      <div className="flex flex-col items-center">
        <ActionBtn onClick={toggleLike} label={liked ? 'Unlike' : 'Like'}>
          <i className={`fa-solid fa-heart ${liked ? 'text-red-500' : 'text-white'}`} style={{ fontSize: 22 }} />
        </ActionBtn>
        <Count n={likes} />
      </div>

      <div className="flex flex-col items-center">
        <ActionBtn onClick={openComments} label="Comments">
          <i className="fa-solid fa-comment text-white" style={{ fontSize: 22 }} />
        </ActionBtn>
        <Count n={null} />
      </div>

      <div className="flex flex-col items-center">
        <ActionBtn onClick={toggleSave} label={saved ? 'Unsave' : 'Save'}>
          <i className={`fa-solid fa-floppy-disk ${saved ? 'text-sky-300' : 'text-white'}`} style={{ fontSize: 20 }} />
        </ActionBtn>
        <Count n={saves} />
      </div>

      <div className="flex flex-col items-center">
        <ActionBtn onClick={doShare} label="Share">
          <i className="fa-solid fa-share text-white" style={{ fontSize: 20 }} />
        </ActionBtn>
        <Count n={shares} />
      </div>

      {/* Gift (bottom) */}
      <div className="flex flex-col items-center" onMouseEnter={prefetchGifts} onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <ActionBtn onClick={openGift} label="Send gift">
          <i className="fa-solid fa-gift" style={{ fontSize: 22, color: '#b222c5' }} />
        </ActionBtn>
      </div>
    </div>
  )
}

